import React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { WodStructure, WodStructureUtils } from '../../types/wod.types';

interface StructurePreviewProps {
  structure: WodStructure;
  expanded?: boolean;
  onToggle?: () => void;
}

export const StructurePreview: React.FC<StructurePreviewProps> = ({
  structure,
  expanded = true,
  onToggle
}) => {
  const { t } = useTranslation();
  const previewText = WodStructureUtils.generatePreview(structure);
  const totalMovements = structure.elements.reduce(
    (sum, el) => sum + WodStructureUtils.getTotalMovements(el),
    0
  );
  const totalReps = structure.elements.reduce(
    (sum, el) => sum + WodStructureUtils.getTotalReps(el),
    0
  );

  if (structure.elements.length === 0) {
    return (
      <Card className="p-6 border-dashed border-border text-center">
        <p className="text-muted-foreground italic text-sm">
          {t('wods.structurePreview.emptyPreview')}
        </p>
      </Card>
    );
  }

  return (
    <Card className="border-border overflow-hidden">
      {/* Header avec toggle */}
      <div
        className={`flex items-center px-4 py-3 bg-muted/50 ${onToggle ? 'cursor-pointer hover:bg-muted/70' : ''}`}
        onClick={onToggle}
      >
        <Eye className="h-5 w-5 text-muted-foreground mr-3" />
        <span className="flex-1 text-sm font-semibold text-foreground uppercase tracking-wide">
          {t('wods.structurePreview.title')}
        </span>

        {/* Stats */}
        <Badge variant="secondary" className="mr-2 font-mono">
          {t('wods.structurePreview.movements', { count: totalMovements })}
        </Badge>

        {totalReps > 0 && (
          <Badge className="mr-2 font-mono font-semibold">
            {t('wods.structurePreview.reps', { count: totalReps })}
          </Badge>
        )}

        {onToggle && (
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
            {expanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <Separator />

      {/* Contenu de l'aperçu */}
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="min-h-0 overflow-hidden p-4 bg-black/50 font-mono text-sm leading-relaxed">
          {previewText.split('\n').map((line, index) => {
            const isGroupHeader = line.includes('Round') || line.includes('AMRAP') || line.includes('Ladder');
            const indentLevel = (line.match(/^\s*/)?.[0].length || 0) * 0.5;

            return (
              <p
                key={index}
                className={`${isGroupHeader ? 'font-semibold' : ''}`}
                style={{ paddingLeft: `${indentLevel}rem` }}
              >
                {line.trim() || '\u00A0'}
              </p>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
